<template>
    <component
        :is="to ? 'router-link' : href ? 'a' : 'button'"
        class="ds-button transition-[height,_padding,_font-size,_gap]"
        :class="[
            `ds-${variant}`,
            `ds-size-${btnSize}`,
            contextClass,
            {
                'ds-loading': loading,
                'ds-square': iconOnly,
            },
        ]"
        v-bind="to ? { to, rel } : { href, target, rel }"
        :disabled="!to && !href ? disabled || loading : undefined"
        :download
        :type="!to && !href ? type : undefined"
    >
        <slot name="leading">
            <DsIcon
                v-if="leading"
                :icon="leading"
                :size="iconSize"
            />
        </slot>

        <div class="relative flex items-center">
            <slot>
                <!-- Button text will appear here -->
            </slot>
            <DsLoadingSpinner
                v-if="loading"
                class="loading-spinner"
                :size="iconSize"
            />
        </div>

        <slot name="trailing">
            <DsIcon
                v-if="trailing"
                :icon="trailing"
                :size="iconSize"
            />
        </slot>
    </component>
</template>

<script setup lang="ts">
import type { BreakpointSize } from '@/components/design-system/design-system';
import { getLargestBreakpointMatch } from '@ds/design-system-breakpoints';
import type { DsIconProps } from '@ds/DsIcon.vue';
import DsIcon from '@ds/DsIcon.vue';
import DsLoadingSpinner from '@ds/spinner/DsSpinner.vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';

export interface DsButtonProps {
    destructive?: boolean;
    disabled?: boolean;
    href?: HTMLAnchorElement['href'] | null;
    to?: RouteLocationRaw;
    iconOnly?: boolean;
    leading?: FontAwesomeIconProps['icon'];
    loading?: boolean;
    onDarkBg?: boolean;
    rel?: HTMLAnchorElement['rel'];
    size: ButtonSize | Partial<Record<BreakpointSize, ButtonSize>>;
    target?: HTMLAnchorElement['target'];
    trailing?: FontAwesomeIconProps['icon'];
    type?: HTMLInputElement['type'];
    variant: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'link-button';
    download?: string;
}

const props = withDefaults(defineProps<DsButtonProps>(), {
    type: 'button',
});

const btnSize = computed<ButtonSize>(() => {
    if (typeof props.size === 'string') return props.size;
    return getLargestBreakpointMatch(props.size) ?? 'md';
});

const ICON_SIZE_BY_BUTTON_SIZE = {
    lg: 'md',
    md: 'md',
    sm: 'sm',
    xs: 'xs',
} as const satisfies Record<ButtonSize, DsIconProps['size']>;

const iconSize = computed(() => ICON_SIZE_BY_BUTTON_SIZE[btnSize.value]);

const contextClass = computed(() => {
    if (props.destructive) return 'ds-context-destructive';
    if (props.onDarkBg) return 'ds-context-on-dark';
    return 'ds-context-default';
});
</script>

<style scoped lang="scss">
@use './styles/ds-button-primary';
@use './styles/ds-button-secondary';
@use './styles/ds-button-tertiary';
@use './styles/ds-button-icon';
@use './styles/ds-button-link';

.ds-button {
    @apply inline-flex min-w-max items-center justify-center rounded-lg;
    transition: background-color 200ms;

    &:focus-visible {
        @apply outline-none ring-4 ring-ds-primary-300;
    }

    &:disabled {
        @apply cursor-not-allowed;
    }

    &.ds-loading {
        @apply cursor-progress;
    }

    // @todo [CLENG-5116] remove
    // prevents legacy global `a` styles from underlining buttons
    // (excluding tertiary and link-button variants)
    &.ds-primary,
    &.ds-secondary,
    &.ds-icon {
        &,
        &:hover,
        &:focus {
            @apply no-underline;
        }
    }
}

.ds-size-lg {
    @apply h-12 gap-2 px-6 text-content-lg;
}

.ds-size-md {
    @apply h-10 gap-2 px-4 text-content-lg;
}

.ds-size-sm {
    @apply h-8 gap-1 px-2 text-content-md;
}

.ds-size-xs {
    @apply h-6 gap-1 rounded px-2 text-content-sm;
}

.ds-square {
    @apply aspect-square items-center justify-center p-0;
}

.loading-spinner {
    @apply absolute inset-0 flex items-center justify-center;
}
</style>
